/** @typedef {import('headlight-configurable-application').Pict} Pict */

/**
 * Entities whose "Printable" manifests describe a QR label layout.
 */
const _PrintableManifestEntities = [ 'Sample', 'Location' ];

/**
 * The product that OWNS the printable manifests (`LIMS-Sample-Printable` et al).
 *
 * They are LIMS configuration, not per-consumer configuration: a form document, the panels and the LIMS
 * app all print the same customer's label. Left unset the loader would look them up under the hosting
 * app's own product (e.g. `HeadlightDynamicForm-Sample-Printable`), find nothing, and silently fall back
 * to the built-in layout. Duplicating the manifests per product would mean keeping N copies of one
 * customer's label in sync, so the product is named here instead.
 */
const _PrintableManifestProduct = 'LIMS';

const libManifestPackagingService = require('manifest-packaging-service');

const PictProvider = require('pict-provider');
const QRCodeCodec = require('headlight-qrcode/source/Headlight-QRCode-Codec.js');

const _LabelCodec = new QRCodeCodec();

// The "tall" label: a fixed drawing width, the code's share of it, the characters per printed line (which sets the
// text size), and how many printed lines one value may wrap onto before it is cut with "...".
const TALL_LABEL_WIDTH = 1200;
const TALL_LABEL_CODE_SHARE = 0.72;
const TALL_LABEL_LINE_LENGTH = 26;
const TALL_LABEL_MAX_WRAP_LINES = 3;

// Inline data URI for the HeadLight logo so QR code generation works in
// offline (iOS WKWebView) contexts where /images/ paths are unavailable.
//TODO: this is to support offline but is a bit hard to support - see if we can make this more robust
const HEADLIGHT_LOGO_BLACK_DATA_URI = 'data:image/png;base64,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';

class Dialog
{
	/**
	 *
	 * @param {Object} options
	 * @param {Object} options.body - The body of the dialog, can be a string or an HTMLElement
	 * @param {boolean} [options.closeOnBackdropClick=true] - Whether the dialog should close when the backdrop is clicked
	 */
	constructor(options)
	{
		this._dialog = document.createElement('div');
		Object.assign(this._dialog.style, {
			display: 'none',
			position: 'fixed',
			left: '50%',
			marginLeft: '-150px',
			width: '300px',
			top: '100px',
			zIndex: 1001,
			background: 'white',
		});

		this._dropShadow = document.createElement('div');
		Object.assign(this._dropShadow.style, {
			display: 'none',
			position: 'fixed',
			left: '0',
			top: '0',
			right: '0',
			bottom: '0',
			zIndex: 1000,
			backdropFilter: 'grayscale(1) blur(3px)',
			background: '#333333ba',
		});
		document.body.appendChild(this._dialog);
		document.body.appendChild(this._dropShadow);

		if (options.closeOnBackdropClick !== false)
		{
			this._dropShadow.addEventListener('click', () => this.hide());
		}

		if (typeof options.body === 'string')
		{
			this._dialog.innerHTML = options.body;
		}
		else if (options.body instanceof HTMLElement)
		{
			this._dialog.appendChild(options.body);
		}
		else
		{
			throw new Error('Unsupported options.body type: ' + typeof options.body);
		}
	}

	/**
	 * Locks scroll while dialog is open
	 * @return {void}
	 */
	lockScroll()
	{
		if (document.body.getAttribute('scroll-locked') === 'true')
		{
			return;
		}
		document.body.setAttribute('scroll-locked', 'true');
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'relative';
	}

	/**
	 * Unlocks scroll when dialog is closed.
	 */
	unlockScroll()
	{
		document.body.removeAttribute('scroll-locked');
		document.body.style.overflow = '';
		document.body.style.position = '';
	}

	/**
	 * Shows the popup dialog.
	 */
	show()
	{
		this._dialog.style.display = 'block';
		this._dropShadow.style.display = 'block';
		this.lockScroll();
	}

	/**
	 * Hides the popup dialog.
	 */
	hide()
	{
		this._dialog.style.display = 'none';
		this._dropShadow.style.display = 'none';
		this.unlockScroll();
	}

	/**
	 * Destroys the popup dialog.
	 */
	destroy()
	{
		if (this._dialog.parentElement)
		{
			this._dialog.parentElement.removeChild(this._dialog);
		}
	}
}

// Documentation: https://scanapp.org/html5-qrcode-docs/docs/apis

class QRCodeProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this._dialog;
		this._qrCodeScanner;
		this._hiddenLayer;
		this._canvas;
		this._qrCodeContainer;
		this._printFrame;

		this.typeConfig = {};

		this.readerActive = false;

		this.printStyle = pOptions.printStyle || 'thin';
		this.customerLogo = null;
		this.getHeadlightLogo = null;

		this.lastCameraEvent =
		{
			time: Date.now(),
			content: '',
		};

		this.cameraKey = '';

		this.logosInitialized = false;
	}

	onAfterInitializeAsync(fCallback)
	{
		super.onAfterInitializeAsync((pError) =>
		{
			this.initializeLogos().finally(() => fCallback(pError));
		});
	}

	onBeforeLoadDataAsync(fCallback)
	{
		super.onBeforeLoadDataAsync((pError) =>
		{
			// The logos are only needed when a label prints; start loading them but do not hold up the document.
			this.initializeLogos();
			return fCallback(pError);
		});
	}

	onInitialize()
	{
		if (this.pict.providers.SessionProvider)
		{
			this.pict.providers.SessionProvider.onSessionChange((pNewSession) =>
			{
				delete this.headlightLogo;
				delete this.customerLogo;
				delete this.logosInitialized;
				delete this._logosPromise;
			});
		}
		return super.onInitialize();
	}

	/**
	 * Load the HeadLight and customer logos used on printed labels. Safe to call repeatedly: one load
	 * is shared, and the print paths await it so a label never draws before the logos arrive.
	 * @return {Promise<void>}
	 */
	initializeLogos()
	{
		if (!this.pict.PictApplication.loggedIn || this.logosInitialized)
		{
			return Promise.resolve();
		}
		if (this._logosPromise)
		{
			return this._logosPromise;
		}
		this.headlightLogo = new Image();
		this.headlightLogo.src = HEADLIGHT_LOGO_BLACK_DATA_URI;
		this._logosPromise = this.getCustomerLogo().then((res) =>
		{
			if (res)
			{
				this.customerLogo = new Image();
				this.customerLogo.src = `/1.0/Customer/Logo/${ res.IDCustomer }`;
				this.logosInitialized = true;
			}
		}).catch((err) =>
		{
			console.log('[QRCodeProvider] Error fetching customer logo, leaving logo field empty.');
		}).finally(() =>
		{
			delete this._logosPromise;
		});
		return this._logosPromise;
	}

	/**
	 * The current session, from the SessionProvider's copy when it has one (no extra CheckSession round trip).
	 * @return {Promise<any>}
	 */
	async _resolveSession()
	{
		const tmpSessionProvider = this.pict.providers?.SessionProvider;
		if (tmpSessionProvider && (typeof tmpSessionProvider.getSession === 'function'))
		{
			const tmpSession = await tmpSessionProvider.getSession();
			if (tmpSession)
			{
				return tmpSession;
			}
		}
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON('CheckSession', (pError, pResult) => (pError ? reject(pError) : resolve(pResult)));
		});
	}

	async getCustomerLogo()
	{
		const tmpSession = await this._resolveSession();
		const customerID = tmpSession?.CustomerID || tmpSession?.IDCustomer;
		return new Promise((resolve, reject) =>
		{
			let tmpRequestOptions = (
				{
					url: this.pict.HeadlightRestClient.formatUrl(`Customer/Logo/${ customerID }`),
				});
			tmpRequestOptions = this.pict.HeadlightRestClient._prepareRequestOptions(tmpRequestOptions);
			this.pict.HeadlightRestClient.restClient.getRawText(tmpRequestOptions, (pError, pResponse, pBody) =>
			{
				pError = this.pict.HeadlightRestClient._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					reject(pError);
				}
				resolve(pResponse.statusCode === 200 ? { IDCustomer: customerID, Body: pBody } : null);
			});
		});
	}

	generateNewQRCode(sample)
	{
		const tmpCurrentUser = this.pict.AppData?.CurrentUser || {};
		return this.pict.fable.services.HeadlightQRCodeHash.mintForCustomer(
			{
				IDProject: sample?.IDProject,
				IDUser: tmpCurrentUser.UserID,
				ExternalSyncGUID: this.pict.AppData?.FullUserRecord?.ExternalSyncGUID,
				CustomerID: tmpCurrentUser.CustomerID,
				TimeZone: tmpCurrentUser.Timezone,
			});
	}

	/**
	 * Get an entity by its QRCode (label printing).
	 * @param {String} entity
	 * @param {String} qr
	 * @return {Promise<Object|Error>}
	 */
	getByQRCode = async (entity, qr) =>
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${ encodeURIComponent(entity) }s/By/QRCode/${ encodeURIComponent(qr) }`, (error, result) =>
			{
				if (error)
				{
					reject(error);
				}
				else
				{
					// Records sharing a code resolve to the most recently updated, as HeadlightQR.resolve and the links app do.
					if (Array.isArray(result) && result.length > 1)
					{
						console.warn(`[QRCodeProvider] ${ result.length } ${ entity } records share the code "${ qr }"; using the most recently updated.`);
						result = result.slice().sort((pA, pB) => String(pB.UpdateDate || '').localeCompare(String(pA.UpdateDate || '')));
					}
					if (entity === 'Location' && result?.[0]?.ParentIDLocation)
					{
						this.pict.HeadlightRestClient.getJSON(`${ entity }s/By/IDLocation/${ result[0].ParentIDLocation }`, (error, parentResult) =>
						{
							if (error)
							{
								reject(error);
							}
							else
							{
								if (parentResult.length)
								{
									result[0].ParentLocation = parentResult[0];
								}
								resolve(result[0]);
							}
						});
					}
					else if (result.length)
					{
						resolve(result[0]);
					}
					else
					{
						reject(new Error('No Item found with that QR Code.'));
					}
				}
			});
		});
	};

	/**
	 * Generic "get entity by ID" function.
	 * @param {String} entity - the type of entity
	 * @param {Number} ID -  the id of the entity
	 */
	getByID = async (entity, ID) =>
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${ entity }/${ ID }`, (error, result) =>
			{
				if (error)
				{
					reject(error);
				}
				else
				{
					resolve(result);
				}
			});
		});
	};

	/**
	* @typedef {object} QRCodeOptions
	* @property {string} [previewID] - The ID of a DOM element to render the preview into.
	* @property {boolean} [raw] - if true, code is used as is and not put in a URL
	* @property {string} [type] - If passed, the passed type will be included in the qr code url
	* @property {string} [printStyle] - the style of label to print, current options are 'thin', 'wide' and 'tall'
	* @property {number} [size] - same as width and height
	* @property {string[]} [footers] - additional text to print below the QR code
	* @property {string[]} [headers] - additional text to print above the QR code
	* @property {string[]} [bigRows] - lists footer rows that should be displayed in "bigger" text
	*/

	/**
	 * A code the camera read: the app's HeadlightQR provider decodes, looks up and hands it to whichever scan
	 * handler is active, exactly as it does for the keyboard scanner.
	 * @param {string} pText
	 */
	receiveCameraScan(pText)
	{
		const tmpQR = this.pict.providers.HeadlightQR;
		if (!tmpQR)
		{
			console.warn('[QRCodeProvider] Camera scan ignored: this app has no HeadlightQR provider.');
			return;
		}
		tmpQR.receiveScan(pText, { Source: 'camera' });
	}

	/**
	 * Passthrough to the "scanQRCode" method.
	 * @param {String} id
	 */
	async scan(id)
	{
		return await this.scanQrCode(id);
	}

	/**
	 * Opens a QR scanner camera instance onto an element with a given id.
	 * @param {String} id
	 * @param {Object} [options] - options for the scanner
	 */
	async openScanner(id, options = {})
	{
		// A new camera session: results from an earlier opening are ignored.
		this.cameraKey = `${ Date.now() }`;
		this.lastCameraEvent = { time: Date.now(), content: '' };
		return await this.scanQrCode(id, true, options);
	}

	/**
	 * Closes any open QR Scanner camera instance.
	 */
	async closeScanner()
	{
		this.cameraKey = '';
		if (!this._qrCodeScanner)
		{
			return;
		}
		return await new Promise((resolve, reject) =>
		{
			async function stopper(tries)
			{
				// @ts-ignore
				const localThis = _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode;
				if (!localThis.canvasElement)
				{
					if (!tries || tries < 10)
					{
						console.log('deferring scanner stop: ', tries);
						setTimeout(()=>
						{
							stopper(tries ? tries + 1 : 1);
						}, 200);
					}
					else
					{
						console.log('Failed to stop camera, exhausted retries.');
						resolve();
					}
					return;
				}
				// @ts-ignore
				if (_Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode && _Pict.providers.QRCodeProvider._qrCodeScanner?.html5Qrcode?.stateManagerProxy?.isScanning())
				{
					// @ts-ignore
					await _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode.stop();
				}
				resolve();
			};
			stopper();
		});
	}

	/**
	 * @param {string} code
	 * @param {QRCodeOptions} [options]
	 * @return {string} - base64 encoded image
	 */
	create(code, options = {})
	{
		const text = options.raw ? code : `${this.pict.providers.UtilityProvider.buildLinksUrl()}${ options.type ? `/${ options.type }` : '' }/qr/${encodeURIComponent(code)}`;
		return this.generateQRCodeImage({
			text: text,
			width: options.size || 256,
			height: options.size || 256,
		});
	}

	/**
	 * Formats strings for the "wide" print layout.
	 * @param {Array<String>} displayStrings
	 * @return {Array<String>}
	 */
	formatStringsForPrinting(displayStrings)
	{
		const finaldisplayStrings = [];
		displayStrings.forEach((s) =>
		{
			const tokens = s.split('COMPUTED_COLUMN');
			if (!tokens?.[1])
			{
				finaldisplayStrings.push(s.length > 80 ? s.substring(0, 77) + '...' : s);
				return;
			}
			if (tokens[0].length > 35)
			{
				tokens[0] = tokens[0].substring(0, 35) + '...';
			}
			if (tokens[1]?.length > 35)
			{
				tokens[1] = tokens[1].substring(0, 35) + '...';
			}
			finaldisplayStrings.push(`${ tokens[0].padEnd(40) }${ tokens[1] }`);
		});
		return finaldisplayStrings;
	}

	/**
	 * Using a Sample record, constructs the set of information to use while printing. Used for the "wide" template.
	 * @param {Object} record
	 * @return {Promise<Array<String>>}
	 */
	async getSamplePrintablesWide(record)
	{
		const formatLocation = (city, state, country) =>
		{
			if (!city && !state && !country)
			{
				return 'N/A';
			}
			else if (city && state)
			{
				return `${ city }, ${ state }`;
			}
			else if ((city && country) || (state && country))
			{
				return `${ city || '---' }, ${ state || '---' }, ${ country || '---' }`;
			}
			else
			{
				return city || state || country;
			}
		};
		const center = 'COMPUTED_COLUMN';
		const sampledByUser = await this.getLabelRecord('User', record.SampledByIDUser || record.CreatingIDUser);
		const displayStrings = [
			`Description: ${ record.Name || 'N/A' }`,
			`Received: ${ record.ReceivedDate ? this.pict.fable.Dates.dayJS(record.ReceivedDate).format('MM/DD/YYYY') : 'N/A' }${ center }Sampled: ${ record.DesignatedTime ? this.pict.fable.Dates.dayJS(record.DesignatedTime).format('MM/DD/YYYY') : 'N/A' }`,
			`Container: ${ record.Container || 'N/A' }${ center }Container Count: ${ record.ContainerCount || 'N/A' }`,
			`Source: ${ record.ReceivedFrom || 'N/A' }${ center }Source Loc.: ${ formatLocation(record.SourceCity, record.SourceState, record.SourceCountry) }`,
		];
		const currentSampleLocation = parseInt(record?.Extension?.StorageLocation) ? (await this.pict.providers.LocationProvider.getLocationByID(parseInt(record?.Extension?.StorageLocation)).catch(() => null)) : null;
		displayStrings.push(`Storage Loc.: ${ currentSampleLocation?.Name || 'N/A' }${ center }Logged By: ${ this.formatLabelUserName(sampledByUser) }`);
		// Probably applicable to most customers but commenting out for now for paragon.
		/*displayStrings.push(`Material: ${ record.IDMaterial ? (await this.getByID('Material', record.IDMaterial)).Name : 'N/A' }${ center }Location: ${ currentSampleLocations.map((loc)=>{ return `${ loc.Name }${ loc.ParentLocation ? ` linked to: ${ loc.ParentLocation.Name }` : '' }`; }).join(', ') || 'N/A' }`);
		if (record.IDOrganization)
		{
			const org = await this.getByID('Organization', record.IDOrganization);
			displayStrings.push(`Producer: ${ org.Name || 'N/A' }${ center }Producer Location: ${ org.City && org.State ? `${ org.City }, ${ org.State }` : 'N/A' }`);
		}*/
		// Probably applicable to most customers but commenting out for now for paragon.
		/*
		let plantString = '';
		if (record.IDProductionPlant)
		{
			const plant = await this.getByID('ProductionPlant', record.IDProductionPlant);
			plantString += `Plant: ${ plant.Name || 'N/A' }`;
		}
		if (record.IDProject)
		{
			const project = await this.getByID('Project', record.IDProject);
			plantString += plantString ? `${ center }Project: ${ project.Name || 'N/A' }` : `Project: ${ project.Name || 'N/A' }`;
		}
		if (plantString)
		{
			displayStrings.push(plantString);
		}*/
		displayStrings.push(`Notes: ${ record.Notes || 'N/A' }`);
		return this.formatStringsForPrinting(displayStrings);
	}

	/**
	 * Using a Location record, constructs the set of information to use while printing. Used for the "wide" template.
	 * @param {Object} record
	 * @return {Array<String>}
	 */
	getLocationPrintablesWide(record)
	{
		//const center = 'COMPUTED_COLUMN';
		const displayStrings =
		[
			`Name: ${ record.Name || 'N/A' }`,
		];
		return this.formatStringsForPrinting(displayStrings);
	}

	/**
	 * Using a Sample record, constructs the set of information to use while printing.
	 * @param {Object} record
	 * @param {String} [printStyleType]
	 * @return {Promise<Array<String>>}
	 */
	async getSamplePrintables(record, printStyleType)
	{
		const printStyle = printStyleType || this.printStyle;
		if (printStyle === 'wide')
		{
			return await this.getSamplePrintablesWide(record);
		}
		const sampledByUser = await this.getLabelRecord('User', record.SampledByIDUser || record.CreatingIDUser);
		const displayStrings = [
			record.QRCode,
			`Name: ${ record.Name || 'N/A' }`,
			`Sample Date: ${ this.pict.fable.Dates.dayJS(record.DesignatedTime || record.CreateDate).toISOString() }`,
			`Material: ${ (await this.getLabelRecord('Material', record.IDMaterial)).Name || 'N/A' }`,
			`Sampled By: ${ this.formatLabelUserName(sampledByUser) }`,
		];
		const currentSampleLocations = (await this.pict.providers.LocationProvider.getLocationsBySampleID(record.IDSample).catch(() => null)) || [];
		displayStrings.push(`Location: ${ currentSampleLocations.map((loc)=>
		{
			return `${ loc.Name }${ loc.ParentLocation ? ` linked to: ${ loc.ParentLocation.Name }` : '' }`;
		}).join(', ') || 'N/A' }`);
		if (record.IDProject)
		{
			const project = await this.getLabelRecord('Project', record.IDProject);
			displayStrings.push(`Project: ${ project.Name || 'N/A' }`);
		}
		if (record.IDOrganization)
		{
			const org = await this.getLabelRecord('Organization', record.IDOrganization);
			displayStrings.push(`Producer: ${ org.Name || 'N/A' }`);
			displayStrings.push(`Producer Location: ${ org.City && org.State ? `${ org.City }, ${ org.State }` : 'N/A' }`);
		}
		if (record.IDProductionPlant)
		{
			const plant = await this.getLabelRecord('ProductionPlant', record.IDProductionPlant);
			displayStrings.push(`Plant: ${ plant.Name || 'N/A' }`);
		}
		displayStrings.push(`Notes: ${ record.Notes || 'N/A' }`);
		return displayStrings;
	}

	/**
	 * Using a Location record, constructs the set of information to use while printing.
	 * @param {Object} record
	 * @param {String} [printStyleType]
	 * @return {Array<String>}
	 */
	getLocationPrintables(record, printStyleType)
	{
		const printStyle = printStyleType || this.printStyle;
		if (printStyle === 'wide')
		{
			return this.getLocationPrintablesWide(record);
		}
		const headlightLogo = 'HLLOGO';
		const color = (color) =>
		{
			return `COLORBEGIN${ color }COLOREND`;
		};
		const displayStrings =
		[
			`${ headlightLogo } ${ color('darkgrey') }Location`,
		];
		return displayStrings;
	}

	/**
	 * Marks the set of rows to apply a forced larger font to for Samples.
	 * @return {Array<String>}
	 */
	getSampleBigRows()
	{
		return [
			'Description',
		];
	}

	/**
	 * Make sure the per-customer label configuration is loaded into `typeConfig`, once.
	 *
	 * `typeConfig` drives `getTemplatedEntitySettings` — PrintStyle (thin/wide), the title template, and
	 * which fields print in which row/column. Apps that load it at startup (LIMS) leave nothing to do here;
	 * apps that never did (the panels/document context) would otherwise print the built-in fallback layout
	 * and silently ignore the customer's printer configuration.
	 *
	 * Deliberately NOT called from `printDecoratedEntity`: that would also change label output for
	 * consumers which have never loaded printable manifests and do not expect the LIMS layout (e.g.
	 * bridge_inspection_v2 printing through ImprovedTabularRecordSetProvider). Callers opt in.
	 *
	 * Never throws — a failed load leaves the built-in layout in place.
	 * @return {Promise<void>}
	 * @async
	 */
	async ensurePrintableManifests()
	{
		// an app that loaded them at startup has already populated typeConfig; nothing to do
		if (this._PrintableManifestsEnsured || Object.keys(this.typeConfig || {}).length > 0)
		{
			this._PrintableManifestsEnsured = true;
			return;
		}
		this._PrintableManifestsEnsured = true;
		try
		{
			if (!this.pict.ManifestPackagingService)
			{
				this.pict.addAndInstantiateServiceType('ManifestPackagingService', libManifestPackagingService);
			}
			await this.pict.ManifestPackagingService.loadPrintableManifests(_PrintableManifestEntities, {}, _PrintableManifestProduct);
			this.pict.log.info('Loaded the printable manifests for QR labels.');
		}
		catch (pError)
		{
			this.pict.log.warn(`Could not load the printable manifests; QR labels will use the default layout: ${pError?.message || pError}`);
		}
	}

	applyTypeConfig(tc)
	{
        this.typeConfig = Object.assign(this.typeConfig, tc);
	}

	addManifestForType(type, manifest)
	{
        this.typeConfig[type] = manifest;
	}

	_getHumanReadableEntityName(pEntity)
	{
		return pEntity.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space before capital letters
	}

	/**
	 * Adds the joined values a printable manifest can ask for that are not columns on the record itself, fetched only
	 * when the layout has a row for them:
	 * - `_LineItems` (Sample): the names of every LineItem attached through SampleLineItemJoin.
	 * - `_Tests` (Sample): the names of every Test the Sample has a TestInstance of (TestInstanceSampleJoin), each once.
	 * The record gets the names comma separated (for a ValueTemplate); the label prints each name on its own row.
	 * A value that cannot be looked up (no attached records, offline, or no provider in this app) prints as N/A.
	 * Names the caller already has, such as an open form's current selection, are used instead of looking them up.
	 * @param {String} entity
	 * @param {Object} record - decorated in place
	 * @param {Object<String, Array<String>>} [pKnownNames] - Names to print instead of looking them up, by field
	 * @return {Promise<Object<String, Array<String>>>} the names looked up, by field
	 */
	async addJoinedLabelFields(entity, record, pKnownNames)
	{
		const descriptors = this.typeConfig[entity]?.Descriptors || {};
		const joinedFields = (entity !== 'Sample') ? {} :
		{
			_LineItems: { Provider: 'LineItemProvider', Fetch: (pProvider) => pProvider.getLineItemsByIDSample(record.IDSample) },
			_Tests: { Provider: 'TestInstanceProvider', Fetch: (pProvider) => pProvider.getTestsForSample(record.IDSample) },
		};
		const joinedNames = {};
		await Promise.all(Object.keys(joinedFields).filter((f) => descriptors[f] && !(f in record)).map(async (f) =>
		{
			if (Array.isArray(pKnownNames?.[f]))
			{
				joinedNames[f] = pKnownNames[f].filter(Boolean);
				record[f] = joinedNames[f].join(', ') || 'N/A';
				return;
			}
			const provider = this.pict.providers?.[joinedFields[f].Provider];
			const joined = (record.IDSample && provider) ? await joinedFields[f].Fetch(provider).catch(() => null) : null;
			joinedNames[f] = (joined || []).map((j) => j.Name).filter(Boolean);
			record[f] = joinedNames[f].join(', ') || 'N/A';
		}));
		return joinedNames;
	}

	async getTemplatedEntitySettings(entity, record, pKnownNames)
	{
		if (!this.pict.services.HeadlightQuickSummaries || !this.typeConfig[entity])
		{
			return null;
		}
		const returnObject =
		{
			headers: [],
			footers: [],
			bigRows: [],
			printStyle: this.typeConfig[entity].PrintStyle || 'thin',
		};
		const joinedNames = await this.addJoinedLabelFields(entity, record, pKnownNames);
		const processedRecord = await this.pict.services.HeadlightQuickSummaries.processRecord(entity, record, 'Printable', this.typeConfig);
		returnObject.headers = [ this.typeConfig[entity].TitleTemplate ? this.fable.parseTemplate(this.typeConfig[entity].TitleTemplate, processedRecord) : this._getHumanReadableEntityName(entity) ];
		const fields = Object.keys(this.typeConfig[entity].Descriptors);
		const rows = [];
		let minRows = 0;
		for (const f of fields)
		{
			const field = this.typeConfig[entity].Descriptors[f];
			if (field.Row && field.Row > minRows)
			{
				minRows = field.Row;
			}
		}
		for (let x = 0; x < minRows; x++)
		{
			rows.push([]);
		}
		for (const f of fields)
		{
			const field = this.typeConfig[entity].Descriptors[f];
			if ((returnObject.printStyle == 'thin' || returnObject.printStyle == 'tall') && field.Column > 1)
			{
				console.warn(`Field ${ f } was not printed b/c the print style is set to "${ returnObject.printStyle }" and the Column # was greater than 1.`);
				continue;
			}
			if (returnObject.printStyle == 'wide' && field.Column > 2)
			{
				console.warn(`Field ${ f } was not printed b/c the print style is set to "wide" and the Column # was greater than 2.`);
				continue;
			}
			if (!field.Row)
			{
				console.warn(`Field ${ f } was not printed b/c a Row was not specified`);
				continue;
			}
			rows[field.Row - 1].push(Object.assign(field, { FieldAddress: f }));
			rows[field.Row - 1] = rows[field.Row - 1].sort((a, b) =>
			{
				if (a.Column > b.Column)
				{
					return 1;
				}
				else if (b.Column > a.Column)
				{
					return -1;
				}
				return 0;
			});
		}
		const generatePrintableField = (field, joinedName, unlabelled) =>
		{
			const value = joinedName ?? (field.HeadlightPrintable?.ValueTemplate ? this.fable.parseTemplate(field.HeadlightPrintable.ValueTemplate, processedRecord) : (processedRecord[field.FieldAddress] || 'N/A'));
			// A field with a blank label prints its value alone.
			return (field.Name && !unlabelled) ? `${ field.Name }: ${ value }` : `${ value }`;
		};
		// A joined field (_LineItems, _Tests) without a ValueTemplate prints one row per name, labelled on the first only;
		// the rows after it follow on.
		const generatePrintableLines = (row) =>
		{
			if (!row.length)
			{
				return [];
			}
			const names = row[0].HeadlightPrintable?.ValueTemplate ? null : joinedNames[row[0].FieldAddress];
			const lines = (names?.length ? names : [ undefined ]).map((n, i) => generatePrintableField(row[0], n, i > 0));
			if (returnObject.printStyle == 'wide' && row[1])
			{
				lines[0] += `COMPUTED_COLUMN${ generatePrintableField(row[1]) }`;
			}
			return lines;
		};
		const tempFooters = [];
		for (const r of rows)
		{
			tempFooters.push(...generatePrintableLines(r));
		}
		returnObject.footers = this.formatStringsForPrinting(tempFooters);
		return returnObject;
	}

	/**
	 * Prints a QR Code for an entity but decorated with additional information about the entity.
	 * @param {String} entity - The entity type being printed.
	 * @param {String} qr - The QR Code of that entity.
	 * @param {String} [previewID] - Tells the function this is a print preview and shouldn't open a print modal.
	 * 								 This is the DOM id of the container to place the preview in.
	 * @param {String} [printStyleOverride] - Allows overriding of the default printing style.
	 * @param {Object} [pKnownRecord] - The record the screen already has, printed when the lookup by code fails (offline).
	 * @param {Object} [pPrintOptions]
	 * @param {boolean} [pPrintOptions.ShowOptions] - Open Print options (label stock and preview) instead of printing on this device's stock.
	 * @param {Object<String, Array<String>>} [pPrintOptions.JoinedLabelNames] - Joined names to print as given, by field (e.g. `_LineItems`)
	 * @return {Promise}
	 */
	async printDecoratedEntity(entity = 'Sample', qr, previewID, printStyleOverride, pKnownRecord, pPrintOptions)
	{
		const printStyle = printStyleOverride ? printStyleOverride : entity === 'Sample' ? 'wide' : this.printStyle;
		await this.initializeLogos();
		if (this.pict.providers?.HeadlightQR && (typeof this.pict.providers.HeadlightQR.loadCustomerPrintSettings === 'function'))
		{
			await this.pict.providers.HeadlightQR.loadCustomerPrintSettings(this.pict.HeadlightRestClient);
		}
		try
		{
			const record = await this.getByQRCode(entity, qr).catch((pError) =>
			{
				if (pKnownRecord && pKnownRecord.QRCode === qr)
				{
					return Object.assign({}, pKnownRecord);
				}
				throw pError;
			});
			if (this.pict.providers.RecordExtension)
			{
				try
				{
					await this.pict.providers.RecordExtension.extendRecords(entity, [ record ]);
				}
				catch (pError)
				{
					console.log(`[QRCodeProvider] Printing ${ entity } ${ qr } without its extended fields: `, pError);
				}
				if (record.Extended)
				{
					for (const k of Object.keys(record.Extended))
					{
						record[k] = record.Extended[k];
					}
				}
			}
			let settingsObj =
			{
				previewID: previewID,
				type: entity,
				printStyle: printStyle,
				headers: entity !== 'Sample' ? record?.Name ? [ record.Name ] : [ entity ] : [],
				bigRows: this[`get${ entity }BigRows`] ? this[`get${ entity }BigRows`]() : [],
				footers: this[`get${ entity }Printables`] ? await this[`get${ entity }Printables`](record, printStyle) : [ qr ],
				showOptions: !!pPrintOptions?.ShowOptions,
			};
			if (entity === 'Sample')
			{
				settingsObj.headers.push('PSID ' + (record.LabControlNumber || '---'));
			}
			const templatedEntitySettings = await this.getTemplatedEntitySettings(entity, record, pPrintOptions?.JoinedLabelNames).catch((pError) =>
			{
				console.log(`[QRCodeProvider] Printing ${ entity } ${ qr } with the standard layout: `, pError);
				return null;
			});
			// If there is a manifest, allow that to override the printing settings.
			if (templatedEntitySettings)
			{
				settingsObj = Object.assign(settingsObj, templatedEntitySettings);
			}
			return await this.print(qr, settingsObj);
		}
		catch (e)
		{
			console.log(`Error printing ${ entity } with QR code ${ qr }: `, e);
			if (!previewID)
			{
				this.reportPrintProblem(`The label for ${ qr } could not be printed. Check your connection and try again.`);
			}
			return null;
		}
	}

	/**
	 * What a printed label's code carries: the customer's label envelope (QR profile) through the shared codec, the same
	 * encoding as the HeadlightQR print dialog. An entity the codec doesn't know keeps the links URL it always had.
	 * @param {string} pEntity
	 * @param {string} pQRCode
	 * @return {string}
	 */
	getLabelPayload(pEntity, pQRCode)
	{
		const tmpHashService = this.pict.fable?.services?.HeadlightQRCodeHash;
		const tmpProfile = (tmpHashService && typeof tmpHashService.getCustomerProfile === 'function') ? tmpHashService.getCustomerProfile() : null;
		const tmpUtility = this.pict.providers.UtilityProvider;
		try
		{
			return _LabelCodec.encode({ Entity: pEntity, Value: pQRCode, Key: 'qr' }, { Envelope: tmpProfile?.Envelope || 'LegacyLinks', Environment: tmpUtility.getEnvironment() });
		}
		catch (pError)
		{
			const tmpEntitySegment = (pEntity === 'Sample') ? '' : `/${ pEntity }`;
			return `${ tmpUtility.buildLinksUrl() }${ tmpEntitySegment }/qr/${ encodeURIComponent(pQRCode) }`;
		}
	}

	/**
	 * Tell the user a print didn't happen, where the app can show a message.
	 * @param {string} pMessage
	 */
	reportPrintProblem(pMessage)
	{
		const tmpPanels = this.pict.views?.HeadlightPanels;
		if (tmpPanels && typeof tmpPanels.toast === 'function')
		{
			tmpPanels.toast({ message: pMessage, type: 'is-danger' });
			return;
		}
		console.warn(`[QRCodeProvider] ${ pMessage }`);
	}

	/**
	 * Look up a record for a label line; a failed lookup prints "N/A" instead of stopping the print.
	 * @param {String} pEntity
	 * @param {Number} pID
	 * @return {Promise<Object>} the record, or an empty object
	 */
	async getLabelRecord(pEntity, pID)
	{
		if (!pID)
		{
			return {};
		}
		try
		{
			return (await this.getByID(pEntity, pID)) || {};
		}
		catch (pError)
		{
			console.log(`[QRCodeProvider] Label line for ${ pEntity } ${ pID } left blank: `, pError);
			return {};
		}
	}

	/**
	 * @param {Object} pUser
	 * @return {string}
	 */
	formatLabelUserName(pUser)
	{
		const tmpName = `${ pUser?.NameFirst || '' } ${ pUser?.NameLast || '' }`.trim();
		return tmpName || 'N/A';
	}

	/**
	 * @param {string} code
	 * @param {QRCodeOptions} [options]
	 *
	 * @return {Promise<'canceled' | 'printed' | undefined>}
	 */
	print(code, options = {})
	{
		const printStyle = options.printStyle || this.printStyle;
		const text = options.raw ? code : this.getLabelPayload(options.type || 'Sample', code);
		const height = 100;
		const topPadding = printStyle === 'wide' ? 100 : 50;
		const footers = [];
		const headers = [];
		let overallSize = 128;
		if (options.footers?.length)
		{
			for (const subText of options.footers)
			{
				footers.push({
					text: subText,
					padding: [ topPadding, 64, 0, 64 ],
					height: height,
				});
				overallSize += height + topPadding;
			}
		}
		if (options.headers?.length)
		{
			for (const subText of options.headers)
			{
				headers.push({
					text: subText,
					padding: [ 0, 64, topPadding, 64 ],
					height: height,
				});
				overallSize += height + topPadding;
			}
		}
		// This is the max percentage of the sticker the text should take up. QR code will take the remaining space.
		const scaleFactor = printStyle === 'wide' ? 65 : 60;

		// Calculate the size the sticker image needs to be based on the text size and scale factor.
		const adjustedScale = (overallSize / scaleFactor) * 100;

		return this.printQrCode({
			previewID: options.previewID,
			showOptions: options.showOptions,
			text: text,
			width: printStyle === 'wide' ? adjustedScale * (3/2) : adjustedScale,
			height: adjustedScale,
			padding: 64,
			footer: footers,
			header: headers,
			bigRows: options.bigRows || [],
			printStyle: printStyle,
		});
	}

	/* -- Print QR Code Methods ---------------------------------------- */

	/**
	* Converts base64 data to an object url
	* @param {string} b64Data
	* @return {string}
	* @private
	*/
	_toObjectUrl(b64Data)
	{
		if (!b64Data.startsWith('data:'))
		{
			throw new Error('Invalid base64 data, expected data:{contentType};base64,{data}');
		}
		const contentType = b64Data.substring('data:'.length, b64Data.indexOf(';'));
		const content = b64Data.substring(b64Data.indexOf(';base64,') + 8);
		const blob = this._b64toBlob(content, contentType);
		return URL.createObjectURL(blob);
	}

	/**
	* Converts a base64 string to a blob
	* @param {string} b64Data
	* @param {string} contentType
	* @param {number} sliceSize
	* @return {module:buffer.Blob}
	* @private
	*/
	_b64toBlob(b64Data, contentType='', sliceSize=512)
	{
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize)
		{
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++)
			{
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	}

	/**
	* YOU PROBABLY DONT WANT THIS METHOD, USE `print` INSTEAD
	*
	* Prints using the newTab method, this is one of several ways to print a QR code.
	* See the `print` method for more details.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printInNewTab(options)
	{
		const src = this.generateQRCodeImage(options);
		const blobUrl = this._toObjectUrl(src);
		window.open('./Global/qrcode/printTab.html?v=' + blobUrl, '_blank');

		return undefined;
	}

	/**
	* "Prints" a preview of the printout into a DOM element.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printPreview(options)
	{
		const el = document.querySelector(`#${ options.previewID }`);
		// The preview follows the label's print style: a wide label reads horizontally, a thin or tall one vertically.
		// (A wide label prints turned sideways, see generateQRCodeImage; Print options shows the as-printed, true-size
		// label on the chosen stock.)
		const orientation = QRCodeProvider.labelPreviewOrientation(options.printStyle || this.printStyle);
		let src = this.generateQRCodeImage(options);
		const { canvas } = this.getGenerationLayers();
		if (QRCodeProvider.isPreviewTurned(orientation, canvas.width, canvas.height))
		{
			src = this._turnCanvasImage(canvas);
		}
		const blobUrl = this._toObjectUrl(src);
		if (el)
		{
			el.innerHTML = /*html*/`<img class="hl-label-preview hl-label-preview--${ orientation }" data-orientation="${ orientation }" src="${ blobUrl }" />`;
		}

		return undefined;
	}

	/**
	 * How a print style's label is previewed on screen.
	 * @param {string} [pPrintStyle] - 'wide', 'thin' or 'tall'
	 * @return {'horizontal'|'vertical'} 'horizontal' for wide (two columns), 'vertical' for thin and tall (one column)
	 */
	static labelPreviewOrientation(pPrintStyle)
	{
		return (pPrintStyle === 'wide') ? 'horizontal' : 'vertical';
	}

	/**
	 * Whether a drawn label has to be turned a quarter to show in its orientation. A square label never is.
	 * @param {'horizontal'|'vertical'} pOrientation
	 * @param {number} pWidth
	 * @param {number} pHeight
	 * @return {boolean}
	 */
	static isPreviewTurned(pOrientation, pWidth, pHeight)
	{
		if (!pWidth || !pHeight || (pWidth === pHeight))
		{
			return false;
		}
		return (pWidth > pHeight) !== (pOrientation === 'horizontal');
	}

	/**
	 * The label just drawn on the generation canvas, turned a quarter clockwise.
	 * @param {HTMLCanvasElement} pCanvas
	 * @return {string} a PNG data URL
	 */
	_turnCanvasImage(pCanvas)
	{
		const turned = document.createElement('canvas');
		turned.width = pCanvas.height;
		turned.height = pCanvas.width;
		const ctx = turned.getContext('2d');
		ctx.translate(turned.width, 0);
		ctx.rotate(Math.PI / 2);
		ctx.drawImage(pCanvas, 0, 0);
		return turned.toDataURL('image/png');
	}

	/**
	* YOU PROBABLY DONT WANT THIS METHOD, USE `print` INSTEAD
	*
	* Prints using an embedded iframe, this is one of several ways to print a QR code.
	* See the `print` method for more details.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printInline(options)
	{
		const src = this.generateQRCodeImage(options);

		const blobUrl = this._toObjectUrl(src);

		const { printFrame } = this.getGenerationLayers();

		let resolveDeferred;
		const deferred = new Promise(function(resolve, reject)
		{
			resolveDeferred = resolve;
		});

		printFrame.contentWindow.document.open();
		window.onfocus=function()
		{
			resolveDeferred();
		};
		window.addEventListener('focus', function(event)
		{
			resolveDeferred();
		}, { once: true });

		printFrame.contentWindow.document.write(`<html lang="en">
			<head>
				<title>QR Code</title>
				<style>
					html, body {
						overflow-x: hidden;
					}
					
					img {
						height: 100%;
						width: 100%;
						max-height: 600px; /* cant seem to get this to scale right, i give up */
						object-fit: contain;
						flex: 1;
					}
					
					.column {
						display: flex;
						flex-direction: column;
						flex: 1;
					}
					
					button {
						appearance: none;
						display: block;
						width: calc(100% - 32px);
						box-sizing: border-box;
						padding: 12px 16px;
						font-size: 36px;
						background-color: #38b2ff;
						border: 1px solid #008ee3;
						color: white;
						font-weight: bold;
						margin: 16px;
						max-width: 600px;
						align-self: center;
					}
					
					@media print {
						@page {
							size: 62mm 100mm;
							margin: 0cm !important;
							padding: 0cm !important;
						}
					
						html, body {
							padding: 0 !important;
							margin: 0 !important;
							position: relative !important;
							inset: 0 !important;
						}
					
						/*@media print {*/
						/*}*/
						* { margin: 0 !important; padding: 0 !important; }
						#controls, .footer, .footerarea{ display: none; }
						html, body {
							/*changing width to 100% causes huge overflow and wrap*/
							height:100%;
							overflow: hidden;
							background: #FFF;
							font-size: 9.5pt;
							page-break-inside: avoid;
						}
						.template { width: auto; left:0; top:0; }
						li { margin: 0 0 10px 20px !important;}
					
					
						button {
							display: none;
						}
					
						img {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							align-self: initial;
					
							object-fit: contain;
							flex: 0;
						}
					}
				</style>
			</head>
			<body onload="print()">
				<img src="${blobUrl}" alt="${options.text}"/>
			</body>
			</html>`);
		printFrame.contentWindow.document.close();

		return deferred;
	}

	async printNative(options)
	{
		const src = this.generateQRCodeImage(options);
		return this.fable.NativeBridge.printImage({
			jobName: 'QR Code',
			base64Image: src,
		});
	}

	/**
	* Prints a QR code using the best method available for the current browser.
	*
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	async printQrCode(options)
	{
		await this.initializeLogos();
		if (options.previewID)
		{
			return this.printPreview(options);
		}
		const method = this.getPrintMethod();
		if (method === 'native')
		{
			return this.printNative(options);
		}
		else if (method === 'window')
		{
			return this.printInNewTab(options);
		}
		else if (this.pict.views?.['HeadlightQR-PrintDialog'] && this.pict.providers?.HeadlightQR)
		{
			return options.showOptions ? this.printWithStockChoice(options) : this.printOnDeviceStock(options);
		}
		else
		{
			return this.printInline(options);
		}
	}

	/**
	* Prints straight to the browser on this device's label stock (Print options' choice, else the customer's default).
	* @param {QRCodePrinterOptions} options
	* @return {undefined}
	*/
	printOnDeviceStock(options)
	{
		this.pict.providers.HeadlightQR.printLabels([ this._labelImageItem(options) ], { Transport: 'browser' });
		return undefined;
	}

	/**
	* The label drawn for printing, as a HeadlightQR label item.
	* @param {QRCodePrinterOptions} options
	* @return {{ Payload: string, Image: string, ImageWidth: number, ImageHeight: number }}
	*/
	_labelImageItem(options)
	{
		const src = this.generateQRCodeImage(Object.assign({}, options, { previewID: undefined }));
		const { canvas } = this.getGenerationLayers();
		return { Payload: options.text, Image: src, ImageWidth: canvas.width, ImageHeight: canvas.height };
	}

	/**
	* Opens Print options, where the user picks the label stock (becomes this device's stock) and sees the
	* label at true size before printing.
	* @param {QRCodePrinterOptions} options
	* @return {undefined}
	*/
	printWithStockChoice(options)
	{
		this.pict.providers.HeadlightQR.openPrintDialog([ this._labelImageItem(options) ], { Title: 'Print options' });
		return undefined;
	}

	/**
	* Gets the ideal printing method for the given browser.
	*
	* @return {'native'|'inline'|'window'}
	*/
	getPrintMethod()
	{
		const ua = navigator.userAgent;
		const iOS = ua.match(/iPad/i) || ua.match(/iPhone/i);
		const webkit = ua.match(/WebKit/i);
		const isSafariMobile = iOS && webkit && !ua.match(/CriOS/i);

		if (this.fable.NativeBridge.hasNativeSupport())
		{
			return 'native';
		}
		else if (isSafariMobile)
		{
			return 'window';
		}
		else
		{
			return 'inline';
		}
	}

	/**
	 * This is a copy of a method inside the HTML5QRCode lib. It should be exactly the same except it has a fix to handle a bug where it can sometimes call removeChild on an element that doesn't exist.
	 * TODO: Hopefully the library fixes this bug themselves at some point, there is an open issue on their github for it.
	 * @param {Number} [tries] - how many times the scanner has tried to run. WE will give up after a few retries.
	 * @return {Promise<void>}
	 */
	localScannerStop(tries)
	{
		// @ts-ignore
		const localThis = _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode;
		if (!localThis.stateManagerProxy.isScanning())
		{
			console.log('Returning early, scanner is not already running.');
			return;
		}

		const toStoppedStateTransaction = localThis.stateManagerProxy.startTransition(1);

		this.shouldScan = false;
		if (localThis.foreverScanTimeout)
		{
			clearTimeout(localThis.foreverScanTimeout);
		}

		// Removes the shaded region if exists.
		const removeQrRegion = () =>
		{
			if (!localThis.element)
			{
				return;
			}
			const childElement = document.getElementById('qr-shaded-region');
			if (childElement)
			{
				localThis.element.removeChild(childElement);
			}
		};

		return localThis.renderedCamera.close().then(() =>
		{
			localThis.renderedCamera = null;

			if (localThis.element && localThis.canavsElement)
			{
				localThis.element.removeChild(localThis.canvasElement);
				localThis.canvasElement = null;
			}

			removeQrRegion();
			if (localThis.qrRegion)
			{
				localThis.qrRegion = null;
			}
			if (localThis.context)
			{
				localThis.context = null;
			}

			toStoppedStateTransaction.execute();
			try
			{
				localThis.hidePausedState();
			}
			catch (e)
			{
				console.log('Scanner ui element not present, skipping.');
			}
			localThis.isScanning = false;
			return Promise.resolve();
		});
	}

	/* -- QR Code Scan Methods ---------------------------------------- */

	/**
	* Scans a QRCode and gives you back the result
	* @param {String} [id] - optional, an id of an element to inject the scanner onto.
	* @param {Boolean} [keepOpen] - optional, keeps the camera in an open scanning mode.
	* @param {Object} [pOptions] - optional, options for the scanner
	* @return {Promise<String>}
	*/
	async scanQrCode(id, keepOpen, pOptions = {})
	{
		const Html5QrcodeScanner = window.Html5QrcodeScanner;
		if (typeof Html5QrcodeScanner === 'undefined')
		{
			throw new Error('QRCode scanning is not supported. Missing library Html5QrcodeScanner');
		}

		// The scanner draws into one container. A camera opened in a different one (the panels' cart, a page's own
		// cart) gets a scanner of its own; the earlier one's container may not be on the page any more.
		const tmpContainerID = id || 'qrcode-scanner';
		if (this._qrCodeScanner && (this._qrCodeScannerContainerID !== tmpContainerID))
		{
			this._qrCodeScanner = null;
		}
		if (!this._qrCodeScanner)
		{
			if (!id)
			{
				this._dialog = new Dialog({
					closeOnBackdropClick: true,
					body: `<div id="qrcode-scanner" style="width: 100%; height: 100%;"></div>`,
				});
			}
			this._qrCodeScannerContainerID = tmpContainerID;
			this._qrCodeScanner = new window.Html5QrcodeScanner(tmpContainerID, {
				fps: 20,
				rememberLastUsedCamera: true,
				videoConstraints: { facingMode: 'environment' },
				qrbox: {
					width: pOptions?.isPhoneDevice ? 150 : 250,
					height: pOptions?.isPhoneDevice ? 150 : 250,
				},
				// turn off file upload feature, it doesnt work well with iOS devices (safari)
				// enum found at https://github.com/mebjas/html5-qrcode/blob/master/src/core.ts#L76
				supportedScanTypes: [ 0 ],
			});
		}
		// Make sure events can't "persist" between instances of opening the cart (i.e. if an event fires before the cart closes, it should not get picked up if the cart closes and reopens).
		const instanceCameraKey = `${ this.cameraKey }`;
		if (keepOpen)
		{
			this._qrCodeScanner.render((result)=>
			{
				if (this.cameraKey && instanceCameraKey === this.cameraKey && Date.now() - this.lastCameraEvent.time > 1000 && this.lastCameraEvent.content !== result)
				{
					this.lastCameraEvent =
					{
						time: Date.now(),
						content: result,
					};
					this.receiveCameraScan(result);
				}
			});
			// html5-qrcode names its permission/camera-picker section after the container.
			const embedUI = document.querySelector(`#${tmpContainerID}__dashboard_section`);
			// Remove this for now until we have a better solution for detecting mobile.
			/*const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
			if (!isMobile)
			{
				embedUI.classList.add('is-hidden');
			}*/
			if (embedUI && embedUI.innerHTML.includes('Request Camera Permissions'))
			{
				const tmpPermissionButton = document.querySelector('#html5-qrcode-button-camera-permission');
				if (tmpPermissionButton)
				{
					// @ts-ignore
					tmpPermissionButton.click();
				}
			}
			// Replace the stop method with a local version that fixes a bug in it.
			// See method jsdoc comment for more details.
			this._qrCodeScanner.html5Qrcode.stop = this.localScannerStop;
			return Promise.resolve('');
		}
		else
		{
			return await new Promise((resolve, reject) =>
			{
				if (this._dialog)
				{
					this._dialog.show();
				}
				this._qrCodeScanner.render((result) =>
				{
					if (this._dialog)
					{
						try
						{
							this._qrCodeScanner.html5Qrcode.stop();
						}
						catch (e)
						{
							console.error('Error stopping scanner', e);
						}
						this._dialog.hide();
					}
					const tmpDecoded = _LabelCodec.decode(result);
					resolve((tmpDecoded.Kind === 'Entity') ? tmpDecoded.Value : tmpDecoded.Raw);
				});
			});
		}
	}

	/* -- QR Code Generation Methods ---------------------------------------- */

	/**
	 * @typedef {Object} Footer
	 * @property {string} footer.text - The text to put in the footer
	 * @property {number} footer.height - The height of the footer, this will be used to calculate the font size
	 * @property {number|Array<number>} [footer.padding] - The padding around the footer
	 */

	/**
	 * @typedef {Object} Header
	 * @property {string} header.text - The text to put in the header
	 * @property {number} header.height - The height of the header, this will be used to calculate the font size
	 * @property {number|Array<number>} [header.padding] - The padding around the header
	 */

	/**
	* @typedef {Object} QRCodePrinterOptions
	* @property {string} text - The text to encode in the QR code
	* @property {string} [previewID] - The ID of a DOM element to render the print preview into.
	* @property {number} [width] - The width of the QR code
	* @property {number} [height] - The height of the QR code
	* @property {string} [printStyle] - Style of label to print, current options are 'thin' and 'wide'
	* @property {number|Array<number>} [padding] - The padding around the QR code
	*
	* @property {Array<Header>} [header] - The header to put above the QR code
	*
	* @property {Array<Footer>} [footer] - The footer to put below the QR code

	* @property {Array<String>} [bigRows] - lists footer rows that should be displayed in "bigger" text
	*
	*/

	/**
	* Parse a padding format into a common output of top, left, bottom, right
	* @param {number|Array<number>} padding - The padding to parse
	* @return {{top: number, left: number, bottom: number, right: number}}
	*/
	_parsePadding(padding)
	{
		if (Array.isArray(padding))
		{
			return {
				top: padding[0] ?? 0,
				right: padding[1] ?? 0,
				bottom: padding[2] ?? padding[0] ?? 0,
				left: padding[3] ?? padding[1] ?? 0,
			};
		}
		return {
			top: padding || 0,
			bottom: padding || 0,
			left: padding || 0,
			right: padding || 0,
		};
	}

	/**
	 * @typedef {Object} TextMetrics
	 * @property {Number} usedFontSize
	 */

	/**
	* Given the text and the width and height, this will return the ideal font size for the text to fit within the given width and height
	* @param {object} ctx
	* @param {string} text
	* @param {number} width
	* @param {number} height
	* @return {TextMetrics}
	* @private
	*/
	_setIdealFontSize(ctx, text, width, height)
	{
		let size = 0; const step = .5;
		let tmpSize;
		do
		{
			size += step;
			ctx.font = 'bold ' + size + 'px "Noto Sans Mono", monospace';
			tmpSize = ctx.measureText(text);
		} while (tmpSize.width < width && (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent) < height);

		// we can overshoot, check if we did then bring it down a notch
		if (tmpSize.width > width || (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent) > height)
		{
			size -= step;
			ctx.font = 'bold ' + size + 'px "Noto Sans Mono", monospace';
			tmpSize = ctx.measureText(text);
		}

		tmpSize.usedFontSize = size;
		return tmpSize;
	}

	/**
	* Generates a QR code image given the provided options.
	* The QRCode library we use only does the QRCode but It's ridiculous to try and
	* get print styles to conform in a standard way. However, what is a great standard is the
	* canvas library! use that to put stuff where we want then make an image from it
	* @param {QRCodePrinterOptions} options
	* @return {string} - base64 encoded image
	*/
	generateQRCodeImage(options)
	{
		if (typeof window.QRCode === 'undefined')
		{
			throw new Error('QRCode generation is not supported. Missing library QRCode');
		}

		if ((options.printStyle || this.printStyle) === 'tall')
		{
			return this.generateTallLabelImage(options);
		}

		// init if we need to

		const { canvas, qrCodeContainer } = this.getGenerationLayers();

		const printStyle = options.printStyle || this.printStyle;

		if (printStyle === 'wide' && !options.previewID)
		{
			canvas.style.width = options.height+'px';
			canvas.style.height = options.width+'px';
			canvas.style.border = '1px solid black';

			canvas.width = options.height;
			canvas.height = options.width;
		}
		else
		{
			canvas.style.width = options.width+'px';
			canvas.style.height = options.height+'px';
			canvas.style.border = '1px solid black';

			canvas.width = options.width;
			canvas.height = options.height;
		}

		const ctx = canvas.getContext('2d');
		if (printStyle === 'wide' && options?.footer?.length && !options.previewID)
		{
			ctx.translate(canvas.width, 0);
			ctx.rotate(Math.PI/2);
		}
		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, options.width, options.height);

		ctx.save();
		const offset = this._parsePadding(options.padding);
		// given our root padding we want to move everything in by this amount
		// translate the entire thing by that offset
		ctx.translate(offset.left, offset.top);

		let { width, height } = options;
		// adjust the perceived width and height of below by our padding options
		width -= offset.left + offset.right;
		height -= offset.top + offset.bottom;

		const sanitizeText = (string) =>
		{
			const tokens = [ 'HLLOGO', 'COLORBEGIN', 'COLOREND' ];
			for (const t of tokens)
			{
				if (t === 'COLORBEGIN' && string.includes('COLORBEGIN'))
				{
					const fontColor = string.split('COLORBEGIN')[1].split('COLOREND')[0];
					string = string.replace(fontColor, '');
				}
				string = string.replace(t, '');
			}
			return string;
		};

		let ctxFont = 0;
		const textSet = [].concat(options.header || []).concat(options.footer || []);

		const sanitizeTextForTesting = (string) =>
		{
			const double = string.includes('HLLOGO');
			const tokens = [ 'HLLOGO', 'COLORBEGIN', 'COLOREND' ];
			for (const t of tokens)
			{
				if (t === 'COLORBEGIN' && string.includes('COLORBEGIN'))
				{
					const fontColor = string.split('COLORBEGIN')[1].split('COLOREND')[0];
					string = string.replace(fontColor, '');
				}
				string = string.replace(t, '');
			}
			return double && textSet.length > 1 ? string + string : string;
		};

		for (const note of textSet)
		{
			const padding = this._parsePadding(note.padding);
			const tmpInsets = this._setIdealFontSize(
				ctx,
				sanitizeTextForTesting(note.text),
				width - padding.left - padding.right,
				note.height,
			);
			if (tmpInsets.usedFontSize < ctxFont || !ctxFont)
			{
				ctxFont = tmpInsets.usedFontSize;
			}
		}

		let extraBottomOffset = 0;
		if (options?.footer?.length && printStyle === 'wide' && ctxFont)
		{
			ctx.font = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
			if (!options.bigRows)
			{
				options.bigRows = [];
			}
			const tmpSize = ctx.measureText('test');
			const smallFooterSize = (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent + options.footer[0].padding[0]);
			ctx.font = 'bold ' + (ctxFont * 1.5) + 'px "Noto Sans Mono", monospace';
			const tmpSizeBig = ctx.measureText('test');
			const bigFooterSize = (tmpSizeBig.actualBoundingBoxAscent + tmpSizeBig.actualBoundingBoxDescent + options.footer[0].padding[0]);
			const footerBoundsCalc = (smallFooterSize * (options.footer.length - options.bigRows.length)) + (bigFooterSize * options.bigRows.length);
			if (footerBoundsCalc < (height * 0.65))
			{
				extraBottomOffset = (height * 0.65) - footerBoundsCalc;
			}
		}

		const imageInset = this._parsePadding(0);

		if (options.header)
		{
			let headerFont = printStyle === 'wide' ? ctxFont * 2.5 : ctxFont;
			if (printStyle === 'wide' && textSet.length)
			{
				// The code takes the top-right corner (up to 30% of the height, below), so the header has to fit beside it.
				for (const headNote of options.header)
				{
					const padding = this._parsePadding(headNote.padding);
					const tmpFit = this._setIdealFontSize(ctx, sanitizeText(headNote.text), width - (height * 0.3) - padding.left - padding.right, Number.MAX_SAFE_INTEGER);
					headerFont = Math.min(headerFont, tmpFit.usedFontSize);
				}
			}
			ctx.font = 'bold ' + headerFont + 'px "Noto Sans Mono", monospace';
			for (const headNote of options.header)
			{
				const padding = this._parsePadding(headNote.padding);
				const tmpInsets = ctx.measureText(sanitizeText(headNote.text));
				console.log('header insets', tmpInsets);

				imageInset.top += tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent + padding.top + padding.bottom;

				ctx.fillStyle = 'black';

				ctx.fillText(
					sanitizeText(headNote.text),
					printStyle === 'wide' && !this.customerLogo ? padding.left : width / 2 - tmpInsets.width / 2,
					imageInset.top - padding.bottom,
				);
			}
		}
		if (this.customerLogo && printStyle === 'wide')
		{
			ctx.drawImage(this.customerLogo, 0, 0, width / 4, (width / 4) * (this.customerLogo.height / this.customerLogo.width));
		}
		if (printStyle === 'wide')
		{
			ctx.font = 'bold ' + (ctxFont * 0.75) + 'px "Noto Sans Mono", monospace';
			ctx.fillText(
				'Print Date: ' + this.pict.fable.Dates.dayJS(new Date().toISOString()).format('MM/DD/YYYY'),
				(width / 5) * 3,
				height,
			);
		}

		if (options.footer)
		{
			options.footer = options.footer.reverse();
			let currentOffset = height - extraBottomOffset;
			ctx.font = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
			for (const footNote of options.footer)
			{
				// Measure with "N/A" still in place: a line holding only "N/A" is blank below (the grey pass draws it), and blank text has no height.
				const measuredText = sanitizeText(footNote.text);
				const tokens = footNote.text.split('N/A');
				let paddedPlaceholder = '';
				for (let x = 0; x < tokens.length; x++)
				{
					const tempSpacer = ('').padEnd(tokens[x].length);
					paddedPlaceholder = paddedPlaceholder.concat(tempSpacer);
					if ((x + 1) < tokens.length)
					{
						paddedPlaceholder = paddedPlaceholder.concat('N/A');
					}
				}
				footNote.text = tokens.join('   ');
				const bigRow = options.bigRows?.filter((r)=>
				{
					return footNote.text.startsWith(r);
				})?.length;
				let tmpFont = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
				if (bigRow)
				{
					const tmpBigPadding = this._parsePadding(footNote.padding);
					const tmpBigFit = this._setIdealFontSize(ctx, sanitizeText(footNote.text), width - tmpBigPadding.left - tmpBigPadding.right, Number.MAX_SAFE_INTEGER);
					tmpFont = 'bold ' + Math.max(ctxFont, Math.min(ctxFont * 1.5, tmpBigFit.usedFontSize)) + 'px "Noto Sans Mono", monospace';
				}
				ctx.font = tmpFont;
				const padding = this._parsePadding(footNote.padding);
				const tmpInsets = ctx.measureText(measuredText);
				console.log('footer insets', tmpInsets);

				const tmpTextOffset = tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent + padding.top + padding.bottom;
				imageInset.bottom += tmpTextOffset;
				currentOffset -= tmpInsets.actualBoundingBoxDescent;
				// Boxes which are helpful for debugging painting issues
				/*ctx.strokeStyle = 'green';
				ctx.strokeRect(
					width / 2 - tmpInsets.width / 2,
					height - footNote.height,
					tmpInsets.width,
					tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent);

				ctx.strokeStyle = 'red';
				ctx.strokeRect(0, height - footNote.height, width, footNote.height);*/

				let fontColor = 'black';
				if (footNote.text.includes('COLORBEGIN'))
				{
					fontColor = footNote.text.split('COLORBEGIN')[1].split('COLOREND')[0];
				}
				let imageWidth = 0;
				if (footNote.text.includes('HLLOGO'))
				{
					imageWidth = (tmpTextOffset - padding.top + padding.bottom) * (this.headlightLogo.width / this.headlightLogo.height);
					ctx.drawImage(this.headlightLogo, 0, currentOffset - tmpInsets.actualBoundingBoxAscent, imageWidth, tmpTextOffset - padding.top - padding.bottom);
				}
				ctx.fillStyle = fontColor;
				ctx.fillText(
					sanitizeText(footNote.text),
					imageWidth ? imageWidth : (printStyle === 'wide' ? padding.left : width / 2 - tmpInsets.width / 2),
					currentOffset,
				);
				ctx.fillStyle = 'grey';
				ctx.fillText(
					paddedPlaceholder,
					imageWidth ? imageWidth : (printStyle === 'wide' ? padding.left : width / 2 - tmpInsets.width / 2),
					currentOffset,
				);
				currentOffset -= tmpInsets.actualBoundingBoxAscent + padding.top + padding.bottom;
				if (printStyle === 'wide')
				{
					for (let x = 0; x < 10; x++)
					{
						ctx.moveTo(0, currentOffset + padding.top/2 + padding.bottom + x);
						ctx.lineTo(options.previewID ? canvas.width : canvas.height, currentOffset + padding.top/2 + padding.bottom + x);
						ctx.stroke();
					}
				}
			}
		}

		ctx.fillStyle = 'black';

		const availableHeight = printStyle === 'wide' ? height - imageInset.bottom : height - imageInset.top - imageInset.bottom;
		const availableWidth = width - imageInset.left - imageInset.right;

		let boundingRectSize = Math.min(availableWidth, availableHeight);
		if (printStyle === 'wide' && boundingRectSize > (height * 0.3) && textSet.length)
		{
			boundingRectSize = height * 0.3;
		}

		console.log('Done', imageInset);
		// ctx.rect(imageInset.left, imageInset.top, width-imageInset.left-imageInset.right, height-imageInset.top-imageInset.bottom);
		// ctx.stroke();
		for (const child of [ ...qrCodeContainer.children ])
		{
			qrCodeContainer.removeChild(child);
		}
		console.log('boundingRectSize', boundingRectSize);
		new window.QRCode(qrCodeContainer, {
			text: options.text,
			width: boundingRectSize,
			height: boundingRectSize,
			colorDark: '#000000',
			colorLight: '#ffffff',
			correctLevel: window.QRCode.CorrectLevel.H,
		}).makeCode(options.text);
		const qrCodeCanvas = qrCodeContainer.querySelector('canvas');

		// console.log('image', qrCodeCanvas.toDataURL());
		ctx.imageSmoothingEnabled = false;
		ctx.drawImage(
			qrCodeCanvas,
			// shift to middle of bounding box
			printStyle === 'wide' ? imageInset.left + (availableWidth - boundingRectSize) : imageInset.left + (availableWidth - boundingRectSize) / 2,
			printStyle === 'wide' ? 0 : imageInset.top + (availableHeight - boundingRectSize) / 2,
			boundingRectSize,
			boundingRectSize,
		);
		ctx.restore();

		return canvas.toDataURL('image/png');
	}

	/**
	* Draws the "tall" label, top to bottom: the header lines, a large code, then one line per footer.
	* Text is monospaced, sized so TALL_LABEL_LINE_LENGTH characters fill the width; a longer value wraps at its spaces
	* onto up to TALL_LABEL_MAX_WRAP_LINES lines. The image is only as tall as what it holds.
	* @param {QRCodePrinterOptions} options
	* @return {string} - PNG data URL
	*/
	generateTallLabelImage(options)
	{
		const { canvas, qrCodeContainer } = this.getGenerationLayers();
		const width = TALL_LABEL_WIDTH;
		const padding = Math.round(width * 0.04);
		const codeSize = Math.round(width * TALL_LABEL_CODE_SHARE);
		const fontFamily = '"Noto Sans Mono", monospace';
		const toBlocks = (pNotes) => (pNotes || [])
			.map((pNote) => this.wrapLabelText(String(pNote.text || '').replace(/COLORBEGIN.*?COLOREND/g, '').replace(/HLLOGO/g, ''), TALL_LABEL_LINE_LENGTH, TALL_LABEL_MAX_WRAP_LINES))
			.filter((pLines) => pLines.length);
		const headerBlocks = toBlocks(options.header);
		const footerBlocks = toBlocks(options.footer);

		const ctx = canvas.getContext('2d');
		ctx.font = `bold 100px ${ fontFamily }`;
		const fontSize = Math.floor(100 * (width - (padding * 2)) / ctx.measureText('M'.repeat(TALL_LABEL_LINE_LENGTH)).width);
		const lineHeight = Math.round(fontSize * 1.3);
		const blockGap = Math.round(fontSize * 0.25);
		const gap = Math.round(fontSize * 0.5);
		const blocksHeight = (pBlocks) => pBlocks.reduce((pSum, pLines) => pSum + (pLines.length * lineHeight), 0) + (Math.max(0, pBlocks.length - 1) * blockGap);

		const height = padding + blocksHeight(headerBlocks) + (headerBlocks.length ? gap : 0) + codeSize + (footerBlocks.length ? gap : 0) + blocksHeight(footerBlocks) + padding;
		canvas.width = width;
		canvas.height = height;
		canvas.style.width = width + 'px';
		canvas.style.height = height + 'px';

		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, width, height);
		ctx.font = `bold ${ fontSize }px ${ fontFamily }`;
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
		const drawLine = (pLine, pTop) =>
		{
			// Centred as a whole, drawn piece by piece so "N/A" can print grey.
			let tmpX = (width - ctx.measureText(pLine).width) / 2;
			const tmpParts = pLine.split('N/A');
			for (let i = 0; i < tmpParts.length; i++)
			{
				ctx.fillStyle = 'black';
				ctx.fillText(tmpParts[i], tmpX, pTop + (lineHeight / 2));
				tmpX += ctx.measureText(tmpParts[i]).width;
				if (i < tmpParts.length - 1)
				{
					ctx.fillStyle = 'grey';
					ctx.fillText('N/A', tmpX, pTop + (lineHeight / 2));
					tmpX += ctx.measureText('N/A').width;
				}
			}
		};

		const drawBlocks = (pBlocks, pTop) =>
		{
			let tmpTop = pTop;
			pBlocks.forEach((pLines, pIndex) =>
			{
				if (pIndex > 0)
				{
					tmpTop += blockGap;
				}
				for (const line of pLines)
				{
					drawLine(line, tmpTop);
					tmpTop += lineHeight;
				}
			});
			return tmpTop;
		};

		let top = drawBlocks(headerBlocks, padding);
		if (headerBlocks.length)
		{
			top += gap;
		}

		for (const child of [ ...qrCodeContainer.children ])
		{
			qrCodeContainer.removeChild(child);
		}
		new window.QRCode(qrCodeContainer, {
			text: options.text,
			width: codeSize,
			height: codeSize,
			colorDark: '#000000',
			colorLight: '#ffffff',
			correctLevel: window.QRCode.CorrectLevel.H,
		}).makeCode(options.text);
		ctx.imageSmoothingEnabled = false;
		ctx.drawImage(qrCodeContainer.querySelector('canvas'), (width - codeSize) / 2, top, codeSize, codeSize);
		top += codeSize;
		if (footerBlocks.length)
		{
			top += gap;
		}
		drawBlocks(footerBlocks, top);

		return canvas.toDataURL('image/png');
	}

	/**
	* Break text into printed lines of at most pLineLength characters, at spaces where it can (a word longer than a
	* line is split). Past pMaxLines the last line is cut with "...".
	* @param {string} pText
	* @param {number} pLineLength
	* @param {number} pMaxLines
	* @return {Array<string>}
	*/
	wrapLabelText(pText, pLineLength, pMaxLines)
	{
		const lines = [];
		let current = '';
		for (const word of String(pText || '').split(/\s+/).filter((pWord) => pWord))
		{
			let rest = word;
			while (rest)
			{
				const candidate = current ? `${ current } ${ rest }` : rest;
				if (candidate.length <= pLineLength)
				{
					current = candidate;
					rest = '';
				}
				else if (current)
				{
					lines.push(current);
					current = '';
				}
				else
				{
					lines.push(rest.substring(0, pLineLength));
					rest = rest.substring(pLineLength);
				}
			}
		}
		if (current)
		{
			lines.push(current);
		}
		if (lines.length > pMaxLines)
		{
			const last = lines[pMaxLines - 1];
			lines.length = pMaxLines;
			lines[pMaxLines - 1] = `${ (last.length > pLineLength - 3) ? last.substring(0, pLineLength - 3).trimEnd() : last }...`;
		}
		return lines;
	}

	/* -- Utility Methods ---------------------------------------- */

	/**
	* Get the layers used for generating QR codes and printing them.
	* @return {{canvas: HTMLCanvasElement, qrCodeContainer: HTMLDivElement, printFrame: HTMLIFrameElement, hiddenLayer: HTMLDivElement}}
	*/
	getGenerationLayers()
	{
		if (!this._hiddenLayer)
		{
			this._hiddenLayer = document.createElement('div');

			this._hiddenLayer.style.visibility = 'hidden';
			this._hiddenLayer.style.height = '0';
			this._hiddenLayer.style.width = '0';
			this._hiddenLayer.style.position = 'absolute';
			this._hiddenLayer.style.top = '-10000px';
			this._hiddenLayer.style.left = '-10000px';

			this._qrCodeContainer = document.createElement('div');
			this._qrCodeContainer.style.display = 'none';
			this._hiddenLayer.appendChild(this._qrCodeContainer);

			this._canvas = document.createElement('canvas');
			this._hiddenLayer.appendChild(this._canvas);

			this._printFrame = document.createElement('iframe');
			this._hiddenLayer.appendChild(this._printFrame);

			document.body.appendChild(this._hiddenLayer);
		}

		return {
			hiddenLayer: this._hiddenLayer,
			canvas: this._canvas,
			qrCodeContainer: this._qrCodeContainer,
			printFrame: this._printFrame,
		};
	}
}

module.exports = QRCodeProvider;
module.exports.default_configuration = {};
